import styles from '../product-timeline.module.scss';
import LabelView from './step-info/label';
import CancelationPolicyView from './step-info/cancelation-policy';
import TrackingView from './step-info/tracking';
import PaymentInfoView from './step-info/payment-info';
import ActionsView from './step-info/actions';
import {
  ActionIdentifier,
  Timeline,
  TimelineShippingLabelAction,
  TimelineStep,
  TimelineStepStatus,
} from '../interfaces/timeline';
import { clsx } from 'clsx';
import { Order } from '../interfaces/myOrder';
import { Product } from '../interfaces/orderSaleProduct';
import { useAnalyticEvents } from '../fake';
import React, { useEffect, useState } from 'react';
import { isReturnShipmentStep, replaceNToBr } from '../helper';
import ShippingInfo from './step-info/shipping-details/shipping-info';
import { useClientId, useClientShippingFromAddress } from '@/hooks/client';
import { SellerShippingAddress } from '@/types/item';

function boxStyle(status: TimelineStepStatus, lastStep: boolean): string {
  return status === 'ongoing' ||
    status === 'pending' ||
    status === 'cancelled' ||
    (status === 'done' && lastStep === true)
    ? clsx(styles.ordersSales__mainRight__steps__step__box, styles['ordersSales__mainRight__steps__step__box--current'])
    : styles.ordersSales__mainRight__steps__step__box;
}

function titleStyle(status: TimelineStepStatus, lastStep: boolean): string {
  switch (status) {
    case 'done':
      if (lastStep) {
        return clsx(
          styles['ordersSales__mainRight__steps__step__box--text--bold'],
          styles['ordersSales__mainRight__steps__step__box--text--875'],
        );
      } else {
        return clsx(
          styles['ordersSales__mainRight__steps__step__box--text--bold'],
          styles['ordersSales__mainRight__steps__step__box--text--grey'],
          styles['ordersSales__mainRight__steps__step__box--text--875'],
        );
      }
    case 'cancelled':
    case 'ongoing':
    case 'pending':
      return clsx(
        styles['ordersSales__mainRight__steps__step__box--text--bold'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      );
    case 'upcoming':
      return clsx(
        styles['ordersSales__mainRight__steps__step__box--text--bold'],
        styles['ordersSales__mainRight__steps__step__box--text--lightGrey'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      );
    case 'none':
      return '';
  }
}

function messageStyle(status: TimelineStepStatus): string {
  return status === 'upcoming'
    ? styles['ordersSales__mainRight__steps__step__box--text--none']
    : styles['ordersSales__mainRight__steps__step__box--text--875'];
}

function additionalStyle(status: TimelineStepStatus): string {
  return status === 'upcoming'
    ? styles['ordersSales__mainRight__steps__step__box--text--none']
    : clsx(
        styles['ordersSales__mainRight__steps__step__box--text--grey'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      );
}

function dateStyle(status: TimelineStepStatus): string {
  return status === 'upcoming'
    ? clsx(
        styles['ordersSales__mainRight__steps__step__box--text--lightGrey'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      )
    : clsx(
        styles['ordersSales__mainRight__steps__step__box--text--grey'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      );
}

const StepInfo: React.FC<{
  order: Order | null;
  product: Product;
  step: TimelineStep;
  timeline: Timeline;
}> = ({ product, order, step, timeline }) => {
  const { kind, timelineType, analyticsTracking, status: timelineStatus, seller } = timeline;
  const {
    status,
    title,
    message,
    additionalInfo,
    displayDate,
    estimatedDisplayDate,
    nextStepStatus,
    pickUpLocationDetails,
    proofOfDelivery,
    cancellationPolicy,
    identifier,
    note,
    messageKey,
    deliveryType,
    hyperlink,
    shipmentDetails,
    actions,
  } = step;
  const lastStep = nextStepStatus === 'none';
  const date = displayDate || estimatedDisplayDate;
  const shouldShowMessage = message && pickUpLocationDetails == null;
  const userKind = kind === 'Seller' ? 'seller' : 'buyer';
  const isCurrentCard =
    status === 'ongoing' || status === 'pending' || status === 'cancelled' || (status === 'done' && lastStep === true);

  const isQcCard = ['buyerItemAssessment', 'sellerItemAssessment'].includes(identifier);

  const isWaitingToBeShippedCard =
    (timelineStatus === 'shipmentInProgress' && identifier === 'sellerShipment') ||
    (timelineStatus === 'conformityInProgress' && identifier === 'buyerConformity');

  const shippingAction = actions ? actions[ActionIdentifier.shippingLabel] : undefined;

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const sellerAddressData = useClientShippingFromAddress(
    seller?.clientId?.toString() || '',
    seller?.clientShippingAddressId || 0,
    isWaitingToBeShippedCard && !!seller,
  );

  const analyticData = { label: userKind, product_id: product.productId };

  const timelineMessage = hyperlink?.message || message;

  const isReturnShipment = isReturnShipmentStep(identifier);

  useEffect(() => {
    if (isCurrentCard) {
      // current tab
      const section = new URLSearchParams(window.location.search).get('section');
      const source = section === '1' ? 'orders' : 'sales';
      const property = {
        tab_type: source, // Order or Sale
        card_name: identifier,
        status: status,
        message_key: messageKey || null,
        delivery_option: deliveryType || null,
      };

      sendAnalyticEvent('timeline_view', {
        label: userKind,
        property: JSON.stringify(property),
        product_id: product.productId,
      });
    }
  }, []);

  const podMessage = proofOfDelivery ? (
    <p className={additionalStyle(status)}>
      <span dangerouslySetInnerHTML={{ __html: replaceNToBr(proofOfDelivery.message, true) }} />
      <span> </span>
      <span
        className={styles.ordersSales__mainRight__link}
        role="presentation"
        onClick={() => {
          sendAnalyticEvent('timeline_home_delivery_confirmation', { ...analyticData, action: 'proof_of_delivery' });
          window.open(proofOfDelivery.url, '_blank');
        }}
      >
        {proofOfDelivery.title}
      </span>
    </p>
  ) : undefined;

  const sellerShippingAddress: SellerShippingAddress | undefined = sellerAddressData.data
    ? (({
        firstName,
        lastName,
        company: companyName,
        phoneNumber,
        line1: address1,
        line2: address2,
        district,
        city,
        zipCode: zip,
        country,
        province,
      }) => ({
        firstName,
        lastName,
        companyName,
        phoneNumber,
        address1,
        address2,
        district: district ?? null,
        city,
        zip,
        country,
        countryIso2: null,
        province: province ?? null,
      }))(sellerAddressData.data)
    : undefined;

  return (
    <div
      data-cy={`timeline-card-container-${identifier}`}
      className={boxStyle(status, lastStep)}
    >
      <LabelView status={status} />
      <div
        className={titleStyle(status, lastStep)}
        data-cy="timeline-step-title"
      >
        {title}
      </div>
      {shouldShowMessage && (
        <div
          className={messageStyle(status)}
          data-cy="timeline-step-message"
          dangerouslySetInnerHTML={{ __html: replaceNToBr(timelineMessage, true) }}
        />
      )}

      {podMessage}

      {cancellationPolicy && <CancelationPolicyView cancellationPolicy={cancellationPolicy} />}

      {additionalInfo &&
        (isQcCard ? (
          <div
            dangerouslySetInnerHTML={{ __html: replaceNToBr(additionalInfo) }}
            className={styles.ordersSales__mainRight__steps__step__box__paymentInfo__warningMessage}
          />
        ) : (
          <div
            className={additionalStyle(status)}
            dangerouslySetInnerHTML={{ __html: replaceNToBr(additionalInfo) }}
          />
        ))}

      {note && (
        <div
          dangerouslySetInnerHTML={{ __html: replaceNToBr(note, true) }}
          className={styles.ordersSales__mainRight__steps__step__box__paymentInfo__warningMessage}
        />
      )}

      <TrackingView
        productId={product.productId}
        userKind={userKind}
        directShipping={timelineType != null && timelineType === 'directShipping'}
        step={step}
      />

      {step?.payoutDetails && <PaymentInfoView paymentDetail={step?.payoutDetails} />}

      {isWaitingToBeShippedCard && (
        <ShippingInfo
          sellerShippingAddress={sellerShippingAddress}
          shipmentDetails={shipmentDetails}
          // eslint-disable-next-line no-restricted-syntax
          action={Array.isArray(shippingAction) ? undefined : (shippingAction as TimelineShippingLabelAction)}
          isReturnShipment={isReturnShipment}
        />
      )}

      <ActionsView
        order={order}
        product={product}
        step={step}
        analyticsTracking={analyticsTracking || {}}
      />
      {date && <div className={dateStyle(status)}>{date}</div>}
    </div>
  );
};

export default StepInfo;
